/**
 * Central public storefront URL strategy (Phase 1B.5-D2).
 *
 * PLATFORM_SLUG (alias): /api/public/v1/storefront/r/{slug}/...
 * CUSTOM_HOSTNAME:      /api/public/v1/storefront/...  (no /r/{slug})
 *
 * Entry mode chooses URL SHAPE only. Server HostTrust + resolver remain tenant authority.
 * Never put RestaurantId or hostname into a custom tenant header.
 */

import { API_PATHS } from "@/constants/api-paths";
import { WEBSITE_ORDER_CHANNEL } from "@/constants/orderChannel";
import {
  getResolvedSlug,
  usesHostOnlyPublicPaths,
} from "@/config/restaurant-id";

function requirePlatformSlug(slug?: string | null): string {
  const s = (slug ?? getResolvedSlug() ?? "").trim();
  if (!s) {
    throw new Error("Slug is required");
  }
  return s;
}

export function buildPublicBootstrapPath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/bootstrap";
  }
  return `public/v1/storefront/bootstrap/r/${encodeURIComponent(requirePlatformSlug(slug))}`;
}

export function buildPublicRestaurantPath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/restaurant";
  }
  return API_PATHS.storefrontRestaurant(requirePlatformSlug(slug));
}

export function buildPublicMenuPath(
  slug?: string | null,
  orderChannel: number = WEBSITE_ORDER_CHANNEL
): string {
  if (usesHostOnlyPublicPaths()) {
    return `public/v1/storefront/menu?orderChannel=${orderChannel}`;
  }
  return API_PATHS.storefrontMenu(requirePlatformSlug(slug), orderChannel);
}

export function buildPublicDiscountsPath(
  slug?: string | null,
  orderChannel: number = WEBSITE_ORDER_CHANNEL
): string {
  if (usesHostOnlyPublicPaths()) {
    return `public/v1/storefront/discounts?orderChannel=${orderChannel}`;
  }
  return API_PATHS.storefrontDiscounts(requirePlatformSlug(slug), orderChannel);
}

export function buildPublicCompliancePreviewPath(
  slug: string | null | undefined,
  menuItemId: string
): string {
  const mid = menuItemId.trim();
  if (!mid) {
    throw new Error("Menu item id is required");
  }
  if (usesHostOnlyPublicPaths()) {
    return `public/v1/storefront/menu-items/${encodeURIComponent(mid)}/compliance/preview`;
  }
  return API_PATHS.storefrontCompliancePreview(requirePlatformSlug(slug), mid);
}

export function buildPublicBillingPreviewPath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/billing/preview";
  }
  return API_PATHS.storefrontBillingPreview(requirePlatformSlug(slug));
}

export function buildPublicOrderCheckoutPath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/orders/checkout";
  }
  return API_PATHS.storefrontOrderCheckout(requirePlatformSlug(slug));
}

export function buildPublicServiceabilityCheckPath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/serviceability/check";
  }
  return API_PATHS.storefrontServiceabilityCheck(requirePlatformSlug(slug));
}

export function buildPublicLocationResolvePath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/location/resolve";
  }
  return API_PATHS.storefrontLocationResolve(requirePlatformSlug(slug));
}

export function buildPublicHomepagePath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/homepage";
  }
  return API_PATHS.storefrontHomepage(requirePlatformSlug(slug));
}

/** Phase 2 — public online-ordering availability projection (anonymous, read-only). */
export function buildPublicOrderingStatusPath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/ordering/status";
  }
  return API_PATHS.storefrontOrderingStatus(requirePlatformSlug(slug));
}

/**
 * Customer order history for the resolved storefront tenant.
 *
 * Replaces `orders/v1/by-user/{userId}` for My Orders. No user id and no restaurant id appear in the
 * path: the server takes the customer from the authenticated identity and the restaurant from the
 * trusted tenant context, so neither can be widened from the browser.
 */
export function buildPublicCustomerOrdersPath(slug?: string | null): string {
  if (usesHostOnlyPublicPaths()) {
    return "public/v1/storefront/orders";
  }
  return `public/v1/storefront/r/${encodeURIComponent(requirePlatformSlug(slug))}/orders`;
}

/**
 * One customer order at the resolved storefront tenant.
 *
 * Replaces `orders/v1/customer/{orderId}` for My Orders → Order Details. An order belonging to
 * another restaurant is 404 here, so the tenant boundary is enforced before the response is built.
 */
export function buildPublicCustomerOrderDetailPath(
  orderId: string,
  slug?: string | null
): string {
  const id = encodeURIComponent(orderId.trim());
  if (usesHostOnlyPublicPaths()) {
    return `public/v1/storefront/orders/${id}`;
  }
  return `public/v1/storefront/r/${encodeURIComponent(requirePlatformSlug(slug))}/orders/${id}`;
}

/**
 * Refund details for one of the customer's cancelled orders at the resolved storefront tenant.
 *
 * Loaded only when the customer asks for it — the order detail payload carries no refund data.
 * Same tenant and ownership boundary as {@link buildPublicCustomerOrderDetailPath}.
 */
export function buildPublicCustomerOrderRefundDetailsPath(
  orderId: string,
  slug?: string | null
): string {
  return `${buildPublicCustomerOrderDetailPath(orderId, slug)}/refund-details`;
}
